import React, { useEffect, useState, useRef } from 'react';
import { X, Calendar, Clock, MapPin, Video, Users, DollarSign, BookOpen, ChevronDown, Download, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ClassRecord } from '../../data/classes';
import { Button } from '../ui/Button';
import { downloadICS, googleCalendarUrl, outlookCalendarUrl } from '../../utils/calendar';
interface ClassDetailDrawerProps {
  classRecord: ClassRecord | null;
  isOpen: boolean;
  onClose: () => void;
}
export function ClassDetailDrawer({
  classRecord,
  isOpen,
  onClose
}: ClassDetailDrawerProps) {
  const [calendarMenuOpen, setCalendarMenuOpen] = useState(false);
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const calendarMenuRef = useRef<HTMLDivElement>(null);
  // Handle escape key to close
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (calendarMenuOpen) {
          setCalendarMenuOpen(false);
        } else {
          onClose();
        }
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, calendarMenuOpen]);
  // Close the calendar menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (calendarMenuRef.current && !calendarMenuRef.current.contains(e.target as Node)) {
        setCalendarMenuOpen(false);
      }
    };
    if (calendarMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [calendarMenuOpen]);
  // Reset transient calendar UI whenever a different class is opened/closed
  useEffect(() => {
    setCalendarMenuOpen(false);
    setCalendarError(null);
  }, [classRecord, isOpen]);
  if (!classRecord) return null;
  const isCancelled = classRecord.status === 'cancelled';
  const isJon = classRecord.instructorGroup === 'Jon Kichen';
  const handleDownloadIcs = () => {
    const result = downloadICS(classRecord);
    if (!result.ok) {
      setCalendarError(result.message || 'Unable to add this class to your calendar.');
    } else {
      setCalendarError(null);
      setCalendarMenuOpen(false);
    }
  };
  const googleUrl = googleCalendarUrl(classRecord);
  const outlookUrl = outlookCalendarUrl(classRecord);
  return <AnimatePresence data-id="element-92">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/50 z-50 transition-opacity" aria-hidden="true" data-id="element-93" />

          {/* Drawer */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-xl z-50 flex flex-col overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="drawer-title" data-id="element-94">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50" data-id="element-95">
              <h2 id="drawer-title" className="text-lg font-bold text-gray-900" data-id="element-96">
                Class Details
              </h2>
              <button onClick={onClose} className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-purple-500" aria-label="Close panel" data-id="element-97">
                <X className="w-5 h-5" data-id="element-98" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6" data-id="element-99">
              <div className="mb-6" data-id="element-100">
                <div className="flex flex-wrap gap-2 mb-4" data-id="element-101">
                  {isCancelled && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-102">
                      Cancelled
                    </span>}
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-103">
                    {classRecord.format}
                  </span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${isJon ? 'bg-brand-purple-50 text-brand-purple-700 border-brand-purple-200' : 'bg-brand-orange-50 text-brand-orange-800 border-brand-orange-200'}`} data-id="element-104">
                    <span className={`w-2 h-2 rounded-full mr-1.5 ${isJon ? 'bg-brand-purple-600' : 'bg-brand-orange-500'}`} data-id="element-105"></span>
                    {classRecord.instructorGroup}
                  </span>
                </div>
                <h3 className={`text-2xl font-bold mb-2 ${isCancelled ? 'text-gray-500 line-through' : 'text-gray-900'}`} data-id="element-106">
                  {classRecord.title}
                </h3>
                <p className="text-brand-purple-600 font-medium flex items-center" data-id="element-107">
                  <BookOpen className="w-4 h-4 mr-2" data-id="element-108" />
                  {classRecord.ceCategory} ({classRecord.hours} hrs)
                </p>
              </div>

              <div className="space-y-4 mb-8" data-id="element-109">
                <div className="flex items-start" data-id="element-110">
                  <Calendar className="w-5 h-5 mr-3 text-gray-400 mt-0.5" data-id="element-111" />
                  <div data-id="element-112">
                    <p className="text-sm font-medium text-gray-900" data-id="element-113">Date</p>
                    <p className="text-sm text-gray-600" data-id="element-114">
                      {new Date(classRecord.date + 'T00:00:00').toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-115">
                  <Clock className="w-5 h-5 mr-3 text-gray-400 mt-0.5" data-id="element-116" />
                  <div data-id="element-117">
                    <p className="text-sm font-medium text-gray-900" data-id="element-118">Time</p>
                    <p className="text-sm text-gray-600" data-id="element-119">
                      {classRecord.startTime} - {classRecord.endTime}
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-120">
                  {classRecord.format === 'Zoom' ? <Video className="w-5 h-5 mr-3 text-gray-400 mt-0.5" data-id="element-121" /> : <MapPin className="w-5 h-5 mr-3 text-gray-400 mt-0.5" data-id="element-122" />}
                  <div data-id="element-123">
                    <p className="text-sm font-medium text-gray-900" data-id="element-124">
                      Location
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-125">
                      {classRecord.location}
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-126">
                  <Users className="w-5 h-5 mr-3 text-gray-400 mt-0.5" data-id="element-127" />
                  <div data-id="element-128">
                    <p className="text-sm font-medium text-gray-900" data-id="element-129">
                      Instructor
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-130">
                      {classRecord.instructorName}
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-131">
                  <DollarSign className="w-5 h-5 mr-3 text-gray-400 mt-0.5" data-id="element-132" />
                  <div data-id="element-133">
                    <p className="text-sm font-medium text-gray-900" data-id="element-134">Price</p>
                    <p className="text-sm text-gray-600 font-bold" data-id="element-135">
                      {classRecord.price}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6" data-id="element-136">
                <h4 className="text-sm font-bold text-gray-900 mb-2" data-id="element-137">
                  Description
                </h4>
                <p className="text-sm text-gray-700 leading-relaxed" data-id="element-138">
                  {classRecord.description}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50" data-id="element-139">
              <Button variant={isCancelled ? 'outline' : 'primary'} className="w-full mb-3" disabled={isCancelled} data-id="element-140">
                {isCancelled ? 'Class Cancelled' : 'Register Now'}
              </Button>
              {!isCancelled && <div className="relative" ref={calendarMenuRef} data-id="element-141">
                  <Button variant="outline" className="w-full" onClick={() => {
              setCalendarError(null);
              setCalendarMenuOpen(open => !open);
            }} aria-haspopup="menu" aria-expanded={calendarMenuOpen} data-id="element-142">
                    <Calendar className="w-4 h-4 mr-2" data-id="element-143" />
                    Add to Calendar
                    <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${calendarMenuOpen ? 'rotate-180' : ''}`} data-id="element-144" />
                  </Button>

                  <AnimatePresence data-id="element-145">
                    {calendarMenuOpen && <motion.div initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 8
              }} transition={{
                duration: 0.15
              }} className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-md shadow-lg border border-gray-200 overflow-hidden z-10" role="menu" aria-label="Add to calendar options" data-id="element-146">
                        <button type="button" role="menuitem" onClick={handleDownloadIcs} className="w-full flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none" data-id="element-147">
                          <Download className="w-4 h-4 mr-3 text-gray-400" data-id="element-148" />
                          Download .ics file
                        </button>
                        {googleUrl && <a href={googleUrl} target="_blank" rel="noopener noreferrer" role="menuitem" onClick={() => setCalendarMenuOpen(false)} className="w-full flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-t border-gray-100" data-id="element-149">
                            <Calendar className="w-4 h-4 mr-3 text-gray-400" data-id="element-150" />
                            Google Calendar
                          </a>}
                        {outlookUrl && <a href={outlookUrl} target="_blank" rel="noopener noreferrer" role="menuitem" onClick={() => setCalendarMenuOpen(false)} className="w-full flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-t border-gray-100" data-id="element-151">
                            <Calendar className="w-4 h-4 mr-3 text-gray-400" data-id="element-152" />
                            Outlook Calendar
                          </a>}
                      </motion.div>}
                  </AnimatePresence>

                  {calendarError && <p className="mt-2 flex items-start text-sm text-red-600" role="alert" data-id="element-153">
                      <AlertCircle className="w-4 h-4 mr-1.5 mt-0.5 flex-shrink-0" data-id="element-154" />
                      {calendarError}
                    </p>}
                </div>}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}